// ---------------------------------------------------------------
// instalarApp.js — Instalar la web como una app (icono en la pantalla
// de inicio, sin la barra del navegador).
//
// Chrome y Edge avisan UNA vez, al cargar la página, de que la web se
// puede instalar (`beforeinstallprompt`). Si nadie está escuchando en
// ese momento, el aviso se pierde; por eso este archivo se importa
// nada más arrancar (index.js) y se queda con él hasta que alguien
// pulse "Instalar" en Mi perfil. No se llama a `preventDefault`: así
// el navegador sigue ofreciendo su propia sugerencia como siempre.
//
// El iPhone no avisa de nada ni deja instalar desde un botón: allí
// sólo se puede desde el menú Compartir, y lo que se hace es explicarlo.
// ---------------------------------------------------------------
import { useEffect, useState } from "react";

let avisoGuardado = null;
let recienInstalada = false;
const oyentes = new Set();
const avisar = () => oyentes.forEach((f) => f());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    avisoGuardado = e;
    avisar();
  });
  window.addEventListener("appinstalled", () => {
    avisoGuardado = null;
    recienInstalada = true;
    avisar();
  });
}

const abiertaComoApp = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const esIOS = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // El iPad se presenta como un Mac, pero con pantalla táctil
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/**
 * @returns {{ estado: "instalada" | "lista" | "ios" | "manual", instalar: () => Promise<boolean> }}
 *   instalada → ya se está usando como app (o se acaba de instalar)
 *   lista     → el navegador deja instalarla con un botón
 *   ios       → iPhone/iPad: hay que hacerlo desde Compartir
 *   manual    → el navegador no avisa; se explica cómo, por si acaso
 */
export function useInstalarApp() {
  const [, repintar] = useState(0);
  useEffect(() => {
    const f = () => repintar((n) => n + 1);
    oyentes.add(f);
    return () => oyentes.delete(f);
  }, []);

  const instalar = async () => {
    if (!avisoGuardado) return false;
    const aviso = avisoGuardado;
    aviso.prompt();
    const { outcome } = await aviso.userChoice;
    // El aviso sólo sirve una vez, se acepte o no
    avisoGuardado = null;
    avisar();
    return outcome === "accepted";
  };

  const estado =
    abiertaComoApp() || recienInstalada
      ? "instalada"
      : avisoGuardado
      ? "lista"
      : esIOS()
      ? "ios"
      : "manual";

  return { estado, instalar };
}
